import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@coin/shared/auth/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { CopilotKit } from '@copilotkit/angular';
import { first, from, switchMap } from 'rxjs';

@Injectable()
export class ChatStore {
  private readonly authService = inject(AuthService);
  private readonly copilotKit = inject(CopilotKit);
  private readonly overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;

  private readonly _threadId = signal(crypto.randomUUID());
  readonly threadId = this._threadId.asReadonly();

  /** True once the chat has been opened at least once (triggers lazy runtime connection). */
  private readonly _chatOpenedOnce = signal(false);

  /** ID of the currently registered country context entry (CopilotKit has no update — remove + add). */
  private _countryContextId?: string;
  /** Last country pushed to the context — guards against redundant re-adds and transient teardown. */
  private _lastCountry?: string;

  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);

  /** Sticky: once true, stays true — prevents copilot-chat destruction on token refresh flicker. */
  readonly everAuthenticated = toSignal(this.authService.isAuthenticated$.pipe(first(v => !!v)), {
    initialValue: false
  });

  private readonly idToken = toSignal(this.authService.idToken$, { initialValue: '' });
  readonly activeUser = toSignal(
    this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated ? from(this.authService.getActiveUser()) : from(Promise.resolve(undefined))
      )
    )
  );
  readonly user = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const user = this.activeUser();

    if (isAuthenticated && user) {
      return {
        gid: user.gid,
        firstName: user.given_name || user.firstName,
        lastName: user.family_name || user.lastName
      };
    }

    return null;
  });

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();
  private _componentPortal: ComponentPortal<unknown> | null = null;

  constructor() {
    this.syncAuthHeaders();
    this.connectRuntimeLazily();
    this.syncCountryContext();
  }

  /**
   * Send the active user's country to the agent via CopilotKit's context array.
   * The country comes from the decoded ID token / emulation token (AuthUser):
   * emulation uses `country`, the Cognito token uses `custom:country`.
   * CopilotKit has no context update — we remove the previous entry and add a new one.
   * Imperative core.addContext is used over connectAgentContext() because the latter's
   * internal effect does not fire reliably in this setup, leaving the context empty.
   */
  private syncCountryContext(): void {
    effect(() => {
      const user = this.activeUser();
      const country = user?.country ?? user?.['custom:country'];

      // Ignore transient `undefined` (isAuthenticated$ flickers on token refresh) so we never
      // tear the entry down and end up sending an empty context. Only act on real changes.
      if (!country || country === this._lastCountry) {
        return;
      }

      const { core } = this.copilotKit;
      if (this._countryContextId) {
        core.removeContext(this._countryContextId);
      }
      this._lastCountry = country;
      this._countryContextId = core.addContext({
        description: "The user's country (ISO country code)",
        value: country
      });
    });
  }

  /**
   * Sync auth headers with CopilotKit on every token refresh.
   * Safe since @copilotkit/angular 0.3.x: updateRuntime({ headers }) patches existing
   * agents in place (core.setHeaders) and the AgentStore is memoized on agent identity,
   * so header updates no longer tear down the chat state.
   */
  private syncAuthHeaders(): void {
    effect(() => {
      const token = this.idToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      this.copilotKit.updateRuntime({ headers });
    });
  }

  /**
   * Lazy runtime connection: set runtimeUrl (which triggers the /info request) only
   * when the chat has been opened at least once AND a valid auth token is available.
   * This avoids the 401 that occurs on fresh page loads via auth callback where
   * provideCopilotKit() would fire /info before the token is ready.
   */
  private connectRuntimeLazily(): void {
    effect(() => {
      const chatOpened = this._chatOpenedOnce();
      const token = this.idToken();
      if (chatOpened && token) {
        this.copilotKit.updateRuntime({
          runtimeUrl: environment.chatBot.runtimeUrl
        });
      }
    });
  }

  setChatDialogComponent(componentPortal: ComponentPortal<unknown>): void {
    this._componentPortal = componentPortal;
  }

  setOpen(isOpen: boolean): void {
    this._isOpen.set(isOpen);
  }

  clearConversation(): void {
    this._threadId.set(crypto.randomUUID());
  }

  toggle(): void {
    if (!this._componentPortal) {
      console.error('No component portal was set');
      return;
    }

    const isOpen = this._isOpen();

    // Signal that the chat has been opened — triggers lazy CopilotKit runtime connection
    if (!isOpen) {
      this._chatOpenedOnce.set(true);
    }

    if (!this.overlayRef) {
      // First open: create overlay once and attach the component
      const positionStrategy = this.overlay.position().global().bottom('0.25rem').right('0.25rem').top('0.25rem');
      const config: OverlayConfig = {
        hasBackdrop: false,
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'chatbot-overlay-panel'
      };
      this.overlayRef = this.overlay.create(config);
      this.overlayRef.attach(this._componentPortal);
    } else {
      // Subsequent toggles: hide/show without destroying the component so chat state is preserved
      this.overlayRef.overlayElement.style.display = isOpen ? 'none' : '';
    }

    this._isOpen.set(!isOpen);
  }
}
